import { useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import type { DialogContextInterface, DialogRegistration, DialogType } from 'contexts/DialogTriggerContext';

import { DialogTriggerContext } from 'contexts/DialogTriggerContext';

const createDialogRegistryKey = (dialogType: DialogType, targetId: string): string => `${dialogType}:${targetId}`;

interface DialogTriggerProviderProps {
  children: ReactNode;
}

export const DialogTriggerProvider = ({ children }: DialogTriggerProviderProps) => {
  const dialogRegistryRef = useRef(new Map<string, () => void>());
  const subscribersRef = useRef(new Set<() => void>());

  const notifySubscribers = useCallback(() => {
    for (const callback of subscribersRef.current) {
      callback();
    }
  }, []);

  /**
   * Register a dialog trigger function
   * Returns a cleanup function that should be called on component unmount
   */
  const registerDialog = useCallback(
    (registration: DialogRegistration): (() => void) => {
      const registryKey = createDialogRegistryKey(registration.dialogType, registration.targetId);
      dialogRegistryRef.current.set(registryKey, registration.onTrigger);
      notifySubscribers();

      // Development warning for potential memory leaks - immediate feedback
      if (process.env.NODE_ENV === 'development') {
        const timeoutId = setTimeout(() => {
          if (dialogRegistryRef.current.has(registryKey)) {
            // eslint-disable-next-line no-console
            console.warn(
              `DialogTriggerProvider: Dialog registration detected without immediate cleanup for ${registryKey}. ` +
                'Ensure useEffect returns the cleanup function to prevent memory leaks. ' +
                'Example: useEffect(() => { const cleanup = registerDialog({...}); return cleanup; }, [])'
            );
          }
        }, 1000); // Give 1 second for cleanup to register

        return () => {
          clearTimeout(timeoutId);
          dialogRegistryRef.current.delete(registryKey);
          notifySubscribers();
        };
      }

      return () => {
        dialogRegistryRef.current.delete(registryKey);
        notifySubscribers();
      };
    },
    [notifySubscribers]
  );

  /**
   * Trigger a dialog for a specific dialog type and target ID
   * Returns true if successful, false if dialog not registered
   */
  const triggerDialog = useCallback((dialogType: DialogType, targetId: string): boolean => {
    const registryKey = createDialogRegistryKey(dialogType, targetId);
    const callback = dialogRegistryRef.current.get(registryKey);

    if (callback) {
      callback();

      return true;
    }

    return false;
  }, []);

  /**
   * Check if a dialog is currently available for a specific dialog type and target ID
   */
  const isDialogAvailable = useCallback((dialogType: DialogType, targetId: string): boolean => {
    const registryKey = createDialogRegistryKey(dialogType, targetId);

    return dialogRegistryRef.current.has(registryKey);
  }, []);

  /**
   * Get all registered dialog keys (useful for debugging/monitoring)
   */
  const getRegisteredDialogs = useCallback((): Array<string> => [...dialogRegistryRef.current.keys()], []);

  /**
   * Subscribe to registry changes to get notified when dialogs are registered/unregistered
   */
  const subscribeToRegistryChanges = useCallback((callback: () => void): (() => void) => {
    subscribersRef.current.add(callback);

    // Development warning for potential memory leaks - immediate feedback
    if (process.env.NODE_ENV === 'development') {
      // Check on next tick if cleanup was called
      const timeoutId = setTimeout(() => {
        if (subscribersRef.current.has(callback)) {
          // eslint-disable-next-line no-console
          console.warn(
            'DialogTriggerProvider: Subscription detected without immediate cleanup. ' +
              'Ensure useEffect returns the cleanup function to prevent memory leaks. ' +
              'Example: useEffect(() => { const cleanup = subscribeToRegistryChanges(callback); return cleanup; }, [])'
          );
        }
      }, 1000); // Give 1 second for cleanup to register

      return () => {
        clearTimeout(timeoutId);
        subscribersRef.current.delete(callback);
      };
    }

    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const contextValue = useMemo(
    (): DialogContextInterface => ({
      getRegisteredDialogs,
      isDialogAvailable,
      registerDialog,
      subscribeToRegistryChanges,
      triggerDialog,
    }),
    [registerDialog, triggerDialog, isDialogAvailable, getRegisteredDialogs, subscribeToRegistryChanges]
  );

  return <DialogTriggerContext.Provider value={contextValue}>{children}</DialogTriggerContext.Provider>;
};
